import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'Components/UI/Modal';
import Button from 'Components/UI/Button';
import ListContainer from 'Components/UI/Containers/ListContainer/ListContainer';
import { iconFileByExtension } from 'Utils/Icons';
import { Icon } from 'Components/UI';
import { fileDataSizeToBytes, sizeFileToInitials } from 'Utils/toolBox';
import {
  IDeleteFilesRequest,
  IRestoreObsoleteFiles,
} from 'Data/interfaces/Obra/IDeleteFilesRequest';
import { useQueryDeleteFiles } from 'Hooks/files/useQueryDeleteFiles';
import { ButtonsWrapper, Container } from './styles';

export interface IExclusaoArquivos {
  visible?: boolean;
  isLoading?: boolean;
  deleteFilesRequest: IDeleteFilesComponentRequest;
  onClearSelectedFiles: () => void;
  onCancel?: () => void;
}

export interface IDeleteFilesComponentRequest {
  filesToDelete: any;
  FolderId: string;
  ObsoleteFolderId?: string;
  ConstructionSiteId: number;
}

const ExclusaoArquivos: FC<IExclusaoArquivos> = ({
  visible,
  isLoading,
  deleteFilesRequest,
  onClearSelectedFiles,
  onCancel,
}) => {
  const { deleteFiles } = useQueryDeleteFiles({ onClearSelectedFiles });
  const { t } = useTranslation(['listagemArquivos', 'common']);

  const lastObsoleteFiles = deleteFilesRequest.filesToDelete
    .filter((file: any) => (file.Versions && file.Versions?.length))
    .map((obsoleteFile: any) => {
      const versions = obsoleteFile.Versions;
      return versions && versions.length ? versions[0] : null;
    });
  const hasObsoletFiles = lastObsoleteFiles.length > 0;

  const removeFiles = (obsoleteFiles?: IRestoreObsoleteFiles[]) => {
    const FilesId = [];
    let onlyFolder = true;
    let hasFolder = false;
    for (const file of deleteFilesRequest.filesToDelete) {
      FilesId.push(file.Identifier.ApiId);
      if (file.IsFolder) {
        hasFolder = true;
      } else {
        onlyFolder = false;
      }
    }

    const request: IDeleteFilesRequest = {
      ConstructionSiteId: deleteFilesRequest.ConstructionSiteId,
      FilesId,
      RestoreObsoleteFiles: obsoleteFiles || [],
      FolderId: deleteFilesRequest.FolderId,
      hasFolder,
      onlyFolder,
    };

    deleteFiles(request);
  };

  const deleteAndRecoverObsoleteFiles = () => {
    const obsoletFilesRestored: IRestoreObsoleteFiles[] = lastObsoleteFiles.map((obsoletFile: any) => ({
      FileId: obsoletFile?.Identifier.ApiId || '',
      FolderId: deleteFilesRequest.FolderId,
      ObsoleteFolderId: deleteFilesRequest.ObsoleteFolderId || '',
    }));
    removeFiles(obsoletFilesRestored);
  };

  const footerButtons = (
    <ButtonsWrapper>
      {hasObsoletFiles && (
        <Button loading={isLoading} onClick={() => removeFiles()}>
          {t('listagemArquivos:deleteFile.deleteCurrentVersionOnly')}
        </Button>
      )}
      <Button
        type='primary'
        loading={isLoading}
        disabled={isLoading}
        onClick={() => hasObsoletFiles
          ? deleteAndRecoverObsoleteFiles()
          : removeFiles()
        }
      >
        {hasObsoletFiles
          ? t('listagemArquivos:deleteFile.deleteAndRestorePrevious')
          : t('listagemArquivos:deleteFile.confirmDeletePermanently')
        }
      </Button>
    </ButtonsWrapper>
  );

  return (
    <Modal
      title={t('listagemArquivos:deleteFile.title')}
      visible={visible}
      footerButtons={footerButtons}
      footerActive={false}
      width={hasObsoletFiles ? 780 : 600}
      customClassName
      newHeader
      onCancel={onCancel}
    >
      <Container>
        <div className="margin" >
          {t('listagemArquivos:deleteFile.warning')}
        </div>
        {hasObsoletFiles ? (
          <>
            <span className="title-list">
              {t('listagemArquivos:deleteFile.hasObsoleteVersion')}
            </span>
            {lastObsoleteFiles?.map((obsoleteFile: any, index: number) => (
              <ListContainer
                key={`obsoleteFile${index}`}
                height={51}
                className="obsoleteFileWrap"
              >
                <Icon
                  color='cinzaPadrao'
                  icon={iconFileByExtension(obsoleteFile?.Extension || '')}
                  customSize={18}
                />
                <span className="title">{obsoleteFile?.Name}</span>
                <span className="padrigth">
                  {obsoleteFile?.Extension}
                </span>
                <span className="padrigth">
                  {sizeFileToInitials(fileDataSizeToBytes(obsoleteFile))}
                </span>
                <span>
                  {new Date(obsoleteFile?.LastUpdate || '').toLocaleDateString()}
                </span>
              </ListContainer>
            ))}
          </>
        ) : (
          <div>{t('listagemArquivos:deleteFile.confirmContinue')}</div>
        )}
      </Container>
    </Modal>
  );
};

export default ExclusaoArquivos;
